import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Send cookies on every customer API call.
axios.defaults.withCredentials = true;

const CustomerAuthContext = createContext(null);

export const useCustomerAuth = () => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used inside <CustomerAuthProvider>');
  return ctx;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null); // null = checking, false = guest, object = signed in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/customer/me`);
        setCustomer(data);
      } catch {
        setCustomer(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/customer/auth/login`, { email, password });
      setCustomer(data.customer);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Login failed' };
    }
  };

  const signup = async ({ email, password, firstName, lastName, phone }) => {
    try {
      const { data } = await axios.post(`${API}/customer/auth/signup`, {
        email, password, firstName, lastName, phone,
      });
      setCustomer(data.customer);
      return { success: true, linkedBookings: data.linkedBookings };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Signup failed' };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/customer/auth/logout`); } catch { /* noop */ }
    setCustomer(false);
  };

  return (
    <CustomerAuthContext.Provider
      value={{ customer, loading, login, signup, logout, isAuthenticated: !!customer && customer !== false }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};
